import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  GitBranch, Play, Loader2, CheckCircle2, XCircle, Clock,
  AlertTriangle, ChevronDown, ChevronUp, Workflow, BarChart3,
  Plus, X, Save, Trash2, ToggleLeft, ToggleRight, Edit3,
  Timer, Bot, FileText, Calendar, RefreshCw,
} from "lucide-react";

const statusConfig: Record<string, { color: string; bg: string; icon: typeof CheckCircle2; label: string }> = {
  queued: { color: "text-yellow-400", bg: "bg-yellow-500/10", icon: Clock, label: "Na Fila" },
  running: { color: "text-primary", bg: "bg-primary/10", icon: Loader2, label: "Executando" },
  completed: { color: "text-success", bg: "bg-success/10", icon: CheckCircle2, label: "Concluído" },
  failed: { color: "text-red-400", bg: "bg-red-500/10", icon: XCircle, label: "Falhou" },
  cancelled: { color: "text-muted-foreground", bg: "bg-muted/50", icon: AlertTriangle, label: "Cancelado" },
};

const typeLabels: Record<string, string> = {
  site_deploy: "Deploy de Site",
  content_gen: "Geração de Conteúdo",
  ad_campaign: "Campanha de Ads",
  seo_audit: "Auditoria SEO",
  custom: "Personalizado",
};

const schedulePresets = [
  { label: "5 minutos", value: "5m" },
  { label: "15 minutos", value: "15m" },
  { label: "30 minutos", value: "30m" },
  { label: "1 hora", value: "1h" },
  { label: "3 horas", value: "3h" },
  { label: "6 horas", value: "6h" },
  { label: "12 horas", value: "12h" },
  { label: "1 dia", value: "1d" },
];

const YOS_AGENTS = [
  { id: "megan", label: "Megan (Orquestradora)" },
  { id: "ytrader", label: "YTrader (Campanhas)" },
  { id: "publish_manager", label: "Publish Manager (Publicação)" },
  { id: "strategist", label: "Strategist (Estratégias)" },
  { id: "ybuilder", label: "YBuilder (Sites)" },
  { id: "ywriter", label: "YWriter (Conteúdo)" },
  { id: "yresearcher", label: "YResearcher (Pesquisa)" },
  { id: "yoptimizer", label: "YOptimizer (SEO)" },
  { id: "ymonitor", label: "YMonitor (Monitoramento)" },
  { id: "ydeployer", label: "YDeployer (Deploy)" },
  { id: "ydesigner", label: "YDesigner (Design)" },
  { id: "yfinance", label: "YFinance (Finanças)" },
];

export function Pipelines() {
  const [expandedPipeline, setExpandedPipeline] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "custom" as string,
    schedule: "",
    agentId: "",
    promptTemplate: "",
  });
  const utils = trpc.useUtils();

  const { data: pipelinesList, isLoading } = trpc.pipelines.list.useQuery();
  const { data: metrics } = trpc.pipelines.metrics.useQuery();
  const { data: allExecutions } = trpc.pipelines.executions.useQuery({ limit: 50 });

  const createMutation = trpc.pipelines.create.useMutation({
    onSuccess: () => {
      utils.pipelines.list.invalidate();
      utils.pipelines.metrics.invalidate();
      resetForm();
    },
  });

  const updateMutation = trpc.pipelines.update.useMutation({
    onSuccess: () => {
      utils.pipelines.list.invalidate();
      resetForm();
    },
  });

  const toggleMutation = trpc.pipelines.toggleActive.useMutation({
    onSuccess: () => {
      utils.pipelines.list.invalidate();
      utils.pipelines.metrics.invalidate();
    },
  });

  const deleteMutation = trpc.pipelines.delete.useMutation({
    onSuccess: () => {
      utils.pipelines.list.invalidate();
      utils.pipelines.metrics.invalidate();
    },
  });

  const triggerExecution = trpc.pipelines.triggerExecution.useMutation({
    onSuccess: () => {
      utils.pipelines.executions.invalidate();
      utils.pipelines.metrics.invalidate();
    },
  });

  function resetForm() {
    setShowCreateForm(false);
    setEditingId(null);
    setFormData({ name: "", description: "", type: "custom", schedule: "", agentId: "", promptTemplate: "" });
  }

  function startEdit(pipeline: any) {
    setEditingId(pipeline.id);
    setFormData({
      name: pipeline.name,
      description: pipeline.description || "",
      type: pipeline.type,
      schedule: pipeline.schedule || "",
      agentId: pipeline.agentId || "",
      promptTemplate: pipeline.promptTemplate || "",
    });
    setShowCreateForm(true);
  }

  function handleSubmit() {
    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        name: formData.name,
        description: formData.description || undefined,
        schedule: formData.schedule || null,
        agentId: formData.agentId || null,
        promptTemplate: formData.promptTemplate || null,
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        description: formData.description || undefined,
        type: formData.type as any,
        schedule: formData.schedule || undefined,
        agentId: formData.agentId || undefined,
        promptTemplate: formData.promptTemplate || undefined,
      });
    }
  }

  const getExecutionsForPipeline = (pipelineId: string) => {
    return allExecutions?.filter((e) => e.pipelineId === pipelineId) || [];
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Workflow className="w-6 h-6 text-primary" />
            Pipelines
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Workflows de automação com agendamento e execução via agentes YOS
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreateForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          Novo Pipeline
        </button>
      </div>

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{metrics.totalPipelines}</p>
            <p className="text-xs text-muted-foreground">Pipelines</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{metrics.totalExecutions}</p>
            <p className="text-xs text-muted-foreground">Execuções</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-success">{metrics.completed}</p>
            <p className="text-xs text-muted-foreground">Concluídas</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-red-400">{metrics.failed}</p>
            <p className="text-xs text-muted-foreground">Falharam</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-primary">{metrics.successRate}%</p>
            <p className="text-xs text-muted-foreground">Taxa Sucesso</p>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-card border border-primary/20 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              {editingId ? "Editar Pipeline" : "Novo Pipeline"}
            </h3>
            <button onClick={resetForm} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Nome</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: YTrader Campanhas Diárias"
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Tipo</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                disabled={!!editingId}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 disabled:opacity-50"
              >
                {Object.entries(typeLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1">Descrição</label>
            <input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o objetivo deste pipeline..."
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1 flex items-center gap-1">
                <Timer className="w-3.5 h-3.5" /> Agendamento (opcional)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {schedulePresets.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => setFormData({ ...formData, schedule: preset.value })}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                      formData.schedule === preset.value
                        ? "bg-primary/10 border-primary/30 text-primary"
                        : "bg-secondary border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <input
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                placeholder="Ex: 30m, 1h, 6h, 1d"
                className="w-full px-3 py-2 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1 flex items-center gap-1">
                <Bot className="w-3.5 h-3.5" /> Agente YOS (opcional)
              </label>
              <select
                value={formData.agentId}
                onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
              >
                <option value="">Nenhum (usar padrão)</option>
                {YOS_AGENTS.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-muted-foreground mb-1 flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Prompt Template
            </label>
            <textarea
              value={formData.promptTemplate}
              onChange={(e) => setFormData({ ...formData, promptTemplate: e.target.value })}
              placeholder={"Variáveis disponíveis: {{pipeline_name}}, {{date}}, {{time}}, {{datetime}}, {{run_count}}\n\nEx: Execute a rotina de campanhas YTrader para o dia {{date}}. Verifique performance dos últimos 30 minutos e ajuste bids se necessário."}
              rows={5}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 resize-none font-mono"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              O prompt será enviado ao Manus API a cada execução agendada. Use variáveis entre {"{{ }}"} para dados dinâmicos.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={resetForm} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isPending || !formData.name.trim()}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-all"
            >
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isPending ? "Salvando..." : editingId ? "Atualizar" : "Criar Pipeline"}
            </button>
          </div>
        </div>
      )}

      {/* Pipelines List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-5">
              <div className="h-5 bg-muted rounded w-1/3 mb-2" />
              <div className="h-3 bg-muted rounded w-2/3 mb-4" />
              <div className="flex gap-3">
                <div className="h-8 bg-muted rounded w-24" />
                <div className="h-8 bg-muted rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : pipelinesList && pipelinesList.length > 0 ? (
        <div className="space-y-4">
          {pipelinesList.map((pipeline) => {
            const pipelineExecs = getExecutionsForPipeline(pipeline.id);
            const isExpanded = expandedPipeline === pipeline.id;

            return (
              <div key={pipeline.id} className={`bg-card border rounded-xl overflow-hidden transition-colors ${
                pipeline.isActive ? "border-border" : "border-yellow-500/30 opacity-70"
              }`}>
                {/* Pipeline Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        pipeline.isActive ? "bg-primary/10" : "bg-yellow-500/10"
                      }`}>
                        <Workflow className={`w-5 h-5 ${pipeline.isActive ? "text-primary" : "text-yellow-500"}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{pipeline.name}</h3>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs px-2 py-0.5 bg-secondary rounded-full text-secondary-foreground">
                            {typeLabels[pipeline.type] || pipeline.type}
                          </span>
                          <span className={`text-xs ${pipeline.isActive ? "text-success" : "text-muted-foreground"}`}>
                            {pipeline.isActive ? "● Ativo" : "○ Inativo"}
                          </span>
                          {pipeline.schedule && (
                            <span className="text-xs px-2 py-0.5 bg-violet-500/10 text-violet-400 border border-violet-500/20 rounded-full flex items-center gap-1">
                              <Timer className="w-3 h-3" /> {pipeline.schedule}
                            </span>
                          )}
                          {pipeline.agentId && (
                            <span className="text-xs px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full flex items-center gap-1">
                              <Bot className="w-3 h-3" /> {pipeline.agentId}
                            </span>
                          )}
                          {pipeline.runCount ? (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <RefreshCw className="w-3 h-3" /> {pipeline.runCount}x
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => triggerExecution.mutate({ pipelineId: pipeline.id })}
                        disabled={triggerExecution.isPending || !pipeline.isActive}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                        title="Executar agora"
                      >
                        {triggerExecution.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                        Executar
                      </button>
                      <button
                        onClick={() => toggleMutation.mutate({ id: pipeline.id })}
                        className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                        title={pipeline.isActive ? "Desativar" : "Ativar"}
                      >
                        {pipeline.isActive ? (
                          <ToggleRight className="w-4 h-4 text-success" />
                        ) : (
                          <ToggleLeft className="w-4 h-4 text-yellow-400" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(pipeline)}
                        className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                        title="Editar"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Excluir pipeline "${pipeline.name}"?`)) {
                            deleteMutation.mutate({ id: pipeline.id });
                          }
                        }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setExpandedPipeline(isExpanded ? null : pipeline.id)}
                        className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {pipeline.description && (
                    <p className="text-sm text-muted-foreground mt-2 ml-13">
                      {pipeline.description}
                    </p>
                  )}

                  {/* Schedule info */}
                  {pipeline.schedule && (
                    <div className="mt-3 ml-13 flex items-center gap-4 text-xs text-muted-foreground">
                      {pipeline.lastRunAt && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Última: {new Date(pipeline.lastRunAt).toLocaleString("pt-BR")}
                        </span>
                      )}
                      {pipeline.nextRunAt && pipeline.isActive && (
                        <span className="flex items-center gap-1 text-primary">
                          <Calendar className="w-3 h-3" /> Próxima: {new Date(pipeline.nextRunAt).toLocaleString("pt-BR")}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Executions (Expandable) */}
                {isExpanded && (
                  <div className="border-t border-border bg-secondary/40 p-4">
                    <h4 className="text-sm font-medium text-foreground mb-3">Histórico de Execuções</h4>

                    {/* Prompt Template Preview */}
                    {pipeline.promptTemplate && (
                      <div className="mb-3 p-3 bg-background border border-border rounded-lg">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Prompt Template</p>
                        <p className="text-xs text-foreground font-mono whitespace-pre-wrap line-clamp-4">
                          {pipeline.promptTemplate}
                        </p>
                      </div>
                    )}

                    {pipelineExecs.length > 0 ? (
                      <div className="space-y-2">
                        {pipelineExecs.map((exec) => {
                          const cfg = statusConfig[exec.status] || statusConfig.queued;
                          const StatusIcon = cfg.icon;
                          const progress = exec.stepsTotal > 0
                            ? Math.round((exec.stepsCompleted / exec.stepsTotal) * 100)
                            : 0;

                          return (
                            <div key={exec.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg">
                              <StatusIcon className={`w-4 h-4 ${cfg.color} ${exec.status === "running" ? "animate-spin" : ""}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(exec.createdAt).toLocaleString("pt-BR")}
                                  </span>
                                </div>
                                <div className="w-full bg-secondary rounded-full h-1.5">
                                  <div
                                    className={`h-1.5 rounded-full transition-all ${
                                      exec.status === "completed" ? "bg-success" :
                                      exec.status === "failed" ? "bg-red-500" :
                                      "bg-primary"
                                    }`}
                                    style={{ width: `${exec.status === "completed" ? 100 : progress}%` }}
                                  />
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                  <p className="text-[10px] text-muted-foreground">
                                    {exec.stepsCompleted}/{exec.stepsTotal} etapas
                                  </p>
                                  {exec.error && (
                                    <p className="text-[10px] text-red-400 truncate max-w-[200px]">{exec.error}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nenhuma execução registrada para este pipeline.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Workflow className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum pipeline criado</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            Pipelines são workflows de automação que podem ser executados sob demanda ou agendados para execução periódica via agentes YOS.
          </p>
          <button
            onClick={() => { resetForm(); setShowCreateForm(true); }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Criar Primeiro Pipeline
          </button>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Timer className="w-5 h-5 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Agendamento de Pipelines</p>
            <p>
              Configure intervalos como <code className="bg-secondary px-1 py-0.5 rounded text-xs">30m</code>, <code className="bg-secondary px-1 py-0.5 rounded text-xs">1h</code>, <code className="bg-secondary px-1 py-0.5 rounded text-xs">6h</code> ou <code className="bg-secondary px-1 py-0.5 rounded text-xs">1d</code> para execução automática.
              O scheduler verifica pipelines pendentes a cada minuto e cria tasks via Manus API.
            </p>
            <p>
              Use variáveis no prompt template: <code className="bg-secondary px-1 py-0.5 rounded text-xs">{"{{date}}"}</code>, <code className="bg-secondary px-1 py-0.5 rounded text-xs">{"{{time}}"}</code>, <code className="bg-secondary px-1 py-0.5 rounded text-xs">{"{{run_count}}"}</code>, <code className="bg-secondary px-1 py-0.5 rounded text-xs">{"{{pipeline_name}}"}</code>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
